import { supabase } from '../config/supabaseClient';
import { API_BASE_URL } from '../config';

interface ABTestResult {
  campaign_id: string;
  campaign_name: string;
  test_type: 'subject' | 'content' | 'sender_name' | 'send_time';
  winner_metric: 'open_rate' | 'click_rate' | 'reply_rate' | 'conversion_rate';
  user_selected_variant?: string;
  variants: Array<{
    name: string;
    isControl: boolean;
    subject?: string;
    content?: string;
    distribution: number;
    sent_count: number;
    opened_count: number;
    opened_rate: number;
    clicked_count: number;
    clicked_rate: number;
    replied_count: number;
    replied_rate: number;
    conversion_count?: number;
    conversion_rate?: number;
  }>;
}

// New analytics endpoint types
export interface MailboxAnalytics {
  mailbox_id: string;
  since: string;
  totals: {
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    totalReplied: number;
    totalBounced: number;
    totalUnsub: number;
  };
  rates: {
    open_rate: number;
    click_rate: number;
    reply_rate: number;
    bounce_rate: number;
    unsubscribe_rate: number;
  };
}

export interface ReplyIntentDistribution {
  since: string;
  distribution: Record<string, number>;
  rates: Record<string, number>;
}

export interface WarmupTelemetryPoint {
  date: string;
  warmup_sent: number;
  errors: number;
}

export interface WarmupTelemetryResponse {
  series: WarmupTelemetryPoint[];
}

// Adaptive warmup health
export interface WarmupHealthPoint {
  date: string; // ISO date
  score: number; // 0-100
  decision: 'increase' | 'hold' | 'reduce' | 'pause';
  applied_volume?: number;
}

export interface WarmupHealthHistoryResponse {
  mailbox_id: string;
  series: WarmupHealthPoint[];
}

export interface WarmupCurrentHealthResponse {
  mailbox_id: string;
  score: number;
  decision: 'increase' | 'hold' | 'reduce' | 'pause';
  details?: Record<string, any>;
}

// Deliverability results (MVP)
export interface DeliverabilityResults {
  mailbox_id: string;
  domain: string;
  since: string;
  totals: {
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    totalReplied: number;
    totalBounced: number;
    totalUnsub: number;
  };
  rates: {
    open_rate: number;
    click_rate: number;
    reply_rate: number;
    bounce_rate: number;
    unsubscribe_rate: number;
  };
  checks: {
    dkim_pass: boolean | null;
    spf_pass: boolean | null;
    dmarc_pass: boolean | null;
    seed_inbox: 'inbox' | 'spam' | 'unknown' | null;
    sender_score: number;
  };
  recent: any[];
}

export interface RotationMailboxStat {
  assignment_id: string | null;
  mailbox_id: string;
  mailbox_email: string;
  mailbox_name: string;
  mailbox_status: string;
  mailbox_daily_limit: number | null;
  assignment_daily_cap: number | null;
  assignment_status: string | null;
  sent_today: number;
  campaign_sent_today: number;
  cap_remaining: number | null;
  last_used_at?: string | null;
  warning?: string;
  deliverability_trend?: RotationMailboxTrendPoint[];
  depletion_forecast_days?: number | null;
}

export interface RotationHealthSummary {
  strategy: 'single' | 'round_robin' | 'weighted' | 'failover';
  default_mailbox_id: string | null;
  pool_size: number;
  active_assignments: number;
  paused_assignments: number;
  exhausted_assignments: number;
  mailboxes: RotationMailboxStat[];
  warnings: string[];
}

export interface RotationMailboxTrendPoint {
  date: string;
  sent: number;
  opened: number;
  replied: number;
  bounced: number;
}

export interface RotationOverviewCampaign {
  campaign_id: string;
  campaign_name: string;
  strategy: 'single' | 'round_robin' | 'weighted' | 'failover';
  pool_size: number;
  active_assignments: number;
  paused_assignments: number;
  exhausted_assignments: number;
  health: 'healthy' | 'warning';
}

export interface RotationOverviewSummary {
  campaign_count: number;
  campaigns_with_rotation: number;
  exhausted_campaigns: number;
  warning_campaigns: number;
  total_assignments: number;
  active_assignments: number;
  paused_assignments: number;
  exhausted_assignments: number;
  campaigns: RotationOverviewCampaign[];
}

interface AnalyticsOverview {
  aggregate_metrics: {
    total_emails_sent: number;
    total_emails_opened: number;
    total_emails_clicked: number;
    total_emails_replied: number;
    avg_open_rate: string;
    avg_click_rate: string;
    avg_reply_rate: string;
  };
  campaign_metrics: {
    open_rates: Array<{
      campaign_id: string;
      campaign_name: string;
      total_sent: number;
      total_opened: number;
      open_rate: number;
    }>;
    click_rates: Array<{
      campaign_id: string;
      campaign_name: string;
      total_sent: number;
      total_clicked: number;
      click_rate: number;
    }>;
    reply_rates: Array<{
      campaign_id: string;
      campaign_name: string;
      total_sent: number;
      total_replied: number;
      reply_rate: number;
    }>;
  };
  recent_campaigns: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
    updated_at: string;
  }>;
  daily_activity: Array<{
    date: string;
    emails_sent: number;
    emails_opened: number;
    emails_clicked: number;
    emails_replied: number;
  }>;
  ab_tests: Array<{
    id: string;
    name: string;
    description: string | null;
    status: 'draft' | 'active' | 'completed' | 'cancelled';
    test_type: 'subject' | 'content' | 'sender_name' | 'send_time';
    winner_metric: 'open_rate' | 'click_rate' | 'reply_rate' | 'conversion_rate';
    created_at: string;
    updated_at: string;
    campaign_id: string;
    campaign_name: string;
  }>;
  ab_test_metrics: {
    active_tests: number;
    completed_tests: number;
    avg_lift: {
      open_rate: number;
      click_rate: number;
      reply_rate: number;
    };
  };
  rotation_overview?: RotationOverviewSummary;
}

interface CampaignAnalytics {
  campaign_id: string;
  metrics: {
    open_rate: number;
    total_sent: number;
    total_opened: number;
    click_rate: number;
    total_clicked: number;
    reply_rate: number;
    total_replied: number;
  };
  status_breakdown: {
    pending: number;
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
    bounced: number;
    unsubscribed: number;
  };
  daily_activity: Array<{
    date: string;
    emails_sent: number;
    emails_opened: number;
    emails_clicked: number;
    emails_replied: number;
  }>;
  contacts: Array<{
    id: string;
    status: string;
    sent_at: string | null;
    opened_at: string | null;
    clicked_at: string | null;
    replied_at: string | null;
    email_contacts: {
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      company: string | null;
    };
  }>;
  rotation_health?: RotationHealthSummary | null;
}

interface ABTest {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  test_type: 'subject' | 'content' | 'sender_name' | 'send_time';
  winner_metric: 'open_rate' | 'click_rate' | 'reply_rate' | 'conversion_rate';
  winner_variant_id: string | null;
  created_at: string;
  updated_at: string;
  ended_at: string | null;
  campaign_id: string;
  email_campaigns: {
    id: string;
    name: string;
  };
}

interface ABTestVariant {
  id: string;
  distribution_percentage: number;
  variant_id: string;
  email_template_variants: {
    id: string;
    name: string;
    subject: string;
    body: string;
    variant_type: 'subject' | 'content' | 'sender_name' | 'send_time';
    is_control: boolean;
  };
}

interface ABTestDetail extends ABTest {
  metrics: Array<{
    ab_test_id: string;
    user_id: string;
    campaign_id: string;
    test_name: string;
    test_type: string;
    variant_id: string;
    variant_name: string;
    is_control: boolean;
    emails_sent: number;
    emails_opened: number;
    emails_clicked: number;
    emails_replied: number;
    open_rate: number;
    click_rate: number;
    reply_rate: number;
    control_open_rate: number;
    control_click_rate: number;
    control_reply_rate: number;
    open_rate_lift: number;
    click_rate_lift: number;
    reply_rate_lift: number;
  }>;
  variants: ABTestVariant[];
}

interface CreateABTestPayload {
  name: string;
  description?: string;
  campaignId: string;
  testType: 'subject' | 'content' | 'sender_name' | 'send_time';
  winnerMetric: 'open_rate' | 'click_rate' | 'reply_rate' | 'conversion_rate';
  variants: Array<{
    name: string;
    templateId: string;
    subject: string;
    body: string;
    isControl: boolean;
    distributionPercentage: number;
  }>;
}

interface UpdateABTestPayload {
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  winnerVariantId?: string;
}

// Helper to construct API URLs with correct structure
const buildApiUrl = (path: string, params?: Record<string, string>): string => {
  // Ensure path starts with a slash and trim any whitespace
  const cleanPath = path.trim();
  const formattedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  // Trim API_BASE_URL to remove any trailing spaces
  const cleanBaseUrl = API_BASE_URL.trim();
  let url = `${cleanBaseUrl}${formattedPath}`;
  
  // Add query parameters if provided
  if (params && Object.keys(params).length > 0) {
    const queryString = Object.entries(params)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
  }
  
  return url;
};

// Helper function to get authentication token
const getToken = async () => {
  const sessionResp = await supabase.auth.getSession();
  const data = (sessionResp as any)?.data || {};
  return data.session?.access_token || '';
};

// Helper functions for fetch API requests
const fetchJSON = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = await getToken();
  
  // Get the user data to extract userId
  const userResp = await supabase.auth.getUser();
  const userData = (userResp as any)?.data;
  const userId = userData?.user?.id;
  
  if (!userId) {
    console.error('No authenticated user found');
    throw new Error('User authentication required');
  }

  // Prepare request options based on method
  const method = options.method || 'GET'; // Default to GET instead of POST
  
  // For GET requests, append userId as query parameter instead of in body
  if (method === 'GET') {
    // Append userId as a query parameter
    url = url + (url.includes('?') ? '&' : '?') + `userId=${encodeURIComponent(userId)}`;
  }
  
  // Prepare the body for non-GET requests
  let body;
  if (method !== 'GET') {
    if (options.body) {
      // If body exists, parse it and add userId
      body = JSON.stringify({
        ...JSON.parse(options.body as string),
        userId
      });
    } else {
      // Create a body with userId
      body = JSON.stringify({ userId });
    }
  }
  
  // Debug logs
  console.log('API Request Details:', {
    url,
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer [TOKEN]' // Don't log the actual token
    },
    body: method !== 'GET' ? body : undefined
  });
  
  const response = await fetch(url, {
    ...options,
    method,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    // Only include body for non-GET requests
    ...(method !== 'GET' && { body })
  });
  
  // Log response status and URL
  console.log(`API Response: ${response.status} ${response.statusText} for ${url}`);
  if (!response.ok) {
    console.error('Response error details:', await response.clone().text());
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `HTTP error! Status: ${response.status}`);
  }
  
  return response.json();
};

const analyticsService = {
  async getABTestResults(): Promise<ABTestResult[]> {
    try {
      // Get user ID from Supabase Auth
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const path = buildApiUrl('/api/email/analytics/abtests', { userId });
      const response = await fetchJSON<ABTestResult[]>(path, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching A/B test results:', error);
      throw error;
    }
  },
  
  async selectABTestWinner(testId: string, variantName: string): Promise<{ success: boolean }> {
    try {
      // Get user ID from Supabase Auth
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const response = await fetchJSON<{ success: boolean }>(
        buildApiUrl('/api/email/analytics/abtests/winner'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            testId,
            variantName
          }),
        }
      );
      return response;
    } catch (error) {
      console.error('Error selecting A/B test winner:', error);
      throw error;
    }
  },
  
  async getAnalyticsOverview(): Promise<AnalyticsOverview> {
    return fetchJSON<AnalyticsOverview>(buildApiUrl('/api/email/analytics/overview'));
  },

  async getWarmupTelemetry(mailboxId: string): Promise<WarmupTelemetryResponse> {
    if (!mailboxId) throw new Error('mailboxId is required');
    const path = `/api/email/warmup/telemetry/${encodeURIComponent(mailboxId)}`;
    return fetchJSON<WarmupTelemetryResponse>(buildApiUrl(path));
  },

  async getCampaignAnalytics(campaignId: string): Promise<CampaignAnalytics> {
    return fetchJSON<CampaignAnalytics>(buildApiUrl(`/api/email/analytics/campaign/${campaignId}`));
  },

  async getABTests(): Promise<ABTest[]> {
    return fetchJSON<ABTest[]>(buildApiUrl('/api/email/analytics/ab-tests'));
  },

  async getABTestDetails(testId: string): Promise<ABTestDetail> {
    return fetchJSON<ABTestDetail>(buildApiUrl(`/api/email/analytics/ab-tests/${testId}`));
  },

  async createABTest(payload: CreateABTestPayload): Promise<ABTest> {
    return fetchJSON<ABTest>(buildApiUrl('/email/analytics/ab-tests'), {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  async updateABTestStatus(testId: string, payload: UpdateABTestPayload): Promise<ABTest> {
    return fetchJSON<ABTest>(buildApiUrl(`/email/analytics/ab-tests/${testId}`), {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  },

  // New: mailbox analytics (last 30 days)
  async getMailboxAnalytics(mailboxId: string): Promise<MailboxAnalytics> {
    return fetchJSON<MailboxAnalytics>(buildApiUrl(`/api/email/analytics/mailbox/${encodeURIComponent(mailboxId)}`));
  },

  // New: reply intent distribution
  async getReplyIntentDistribution(): Promise<ReplyIntentDistribution> {
    return fetchJSON<ReplyIntentDistribution>(buildApiUrl('/api/email/analytics/reply-intent'));
  },

  // Deliverability monitoring
  async runSeedCheck(mailboxId: string, domainOverrides?: string[]): Promise<{ jobId: string; accepted: boolean; mailboxId: string; domainOverrides?: string[] }> {
    if (!mailboxId) throw new Error('mailboxId is required');
    const path = '/api/email/deliverability/seed-check';
    return fetchJSON(buildApiUrl(path), {
      method: 'POST',
      body: JSON.stringify({ mailboxId, ...(domainOverrides && domainOverrides.length ? { domainOverrides } : {}) })
    });
  },

  async getDeliverabilityResults(mailboxId: string, sinceDays = 30): Promise<DeliverabilityResults> {
    if (!mailboxId) throw new Error('mailboxId is required');
    const params = { mailboxId, sinceDays: String(Math.max(1, Math.min(90, sinceDays))) };
    const path = buildApiUrl('/api/email/deliverability/results', params);
    return fetchJSON<DeliverabilityResults>(path);
  },

  // Adaptive warmup
  async getWarmupHealthHistory(mailboxId: string): Promise<WarmupHealthHistoryResponse> {
    if (!mailboxId) throw new Error('mailboxId is required');
    const path = buildApiUrl(`/api/email/warmup/health/${encodeURIComponent(mailboxId)}/history`);
    return fetchJSON<WarmupHealthHistoryResponse>(path);
  },

  async getCurrentWarmupHealth(mailboxId: string): Promise<WarmupCurrentHealthResponse> {
    if (!mailboxId) throw new Error('mailboxId is required');
    const path = buildApiUrl(`/api/email/warmup/health/${encodeURIComponent(mailboxId)}/current`);
    return fetchJSON<WarmupCurrentHealthResponse>(path);
  },

  async recomputeWarmupHealth(mailboxId: string): Promise<{ applied: boolean; mailbox_id: string } & WarmupCurrentHealthResponse> {
    if (!mailboxId) throw new Error('mailboxId is required');
    const path = buildApiUrl(`/api/email/warmup/health/${encodeURIComponent(mailboxId)}/recompute`);
    return fetchJSON(path, { method: 'POST' });
  }
};

export default analyticsService;
export type {
  AnalyticsOverview,
  CampaignAnalytics,
  ABTest,
  ABTestDetail,
  CreateABTestPayload,
  UpdateABTestPayload
};
